import { nanoid } from 'nanoid';

import { MetricMeasurement, SuccessCriteria } from '@/services/types';

import {
  DEFAULT_AI_METRICS,
  DEFAULT_PREMAPPED_AI_VARIANTS,
  DEFAULT_RAW_FALSE_VARIANT,
  DEFAULT_RAW_TRUE_VARIANT,
} from './constants';
import {
  AiGeneratedExperimentDocumentContent,
  AiGeneratedFlagVariantDto,
  AiGeneratedMetricDto,
} from './types';

export const STREAM_LOADING = '__STREAM_LOADING__';

export class AiExperimentDtoMapper {
  /**
   * Take the ChatGPT response + request body, and build out an object
   * that will be stuffed into a mongoose document as "content".
   *
   * We may have to clean up the ChatGPT response since we can't guarantee
   * the model's output, even when using structured JSON-schema output.
   * @param response
   */
  static mapGptResponseToDocumentContent(
    response: any,
  ): AiGeneratedExperimentDocumentContent {
    const {
      title = STREAM_LOADING,
      hypothesis = STREAM_LOADING,
      featureFlagName = STREAM_LOADING,
      variants = DEFAULT_PREMAPPED_AI_VARIANTS,
      metrics = DEFAULT_AI_METRICS,
    } = response;

    const prettyName = unKebabToSentence(featureFlagName);
    const key = AiExperimentDtoMapper.buildFlagKey(featureFlagName);

    const mappedVariants = AiExperimentDtoMapper.scrubVariants(variants);
    const mappedMetrics = metrics
      .map(AiExperimentDtoMapper.mapMetric)
      .filter(Boolean) as AiGeneratedMetricDto[];

    return {
      key,
      title,
      hypothesis,
      summary: response.summary ?? STREAM_LOADING,
      featureFlagName: prettyName,
      metrics: mappedMetrics,
      variants: mappedVariants,
      createdAt: new Date().toISOString(),
      events: response.events ?? [],
    };
  }

  /**
   * Map a ChatGPT variant (non-guaranteed shape) to a well-known shape.
   * @param variant
   * @param distribution
   * @private
   */
  private static mapFlagVariant(
    variant: any,
    distribution: number,
  ): AiGeneratedFlagVariantDto {
    return {
      distribution,
      name: variant.name ?? STREAM_LOADING,
      value: variant.value ?? STREAM_LOADING,
    };
  }

  /**
   * Map a ChatGPT metric (non-guaranteed shape) to a well-known shape.
   * @param metric
   * @private
   */
  private static mapMetric(metric: any): AiGeneratedMetricDto | null {
    let measurementType: MetricMeasurement;

    if (metric.type) {
      switch (metric.type) {
        case 'percent':
          measurementType = MetricMeasurement.Percent;
          break;
        case 'sum':
          measurementType = MetricMeasurement.Sum;
          break;
        case 'count':
          measurementType = MetricMeasurement.Count;
          break;
        case 'average':
          measurementType = MetricMeasurement.Average;
          break;
        default:
          console.error(`Unknown metric type: ${metric.type}`);
          console.error('metric', metric);
          return null;
      }

      return {
        measurementType,
        name: metric.name ?? STREAM_LOADING,
        eventKey: metric.eventKey ?? STREAM_LOADING,
        successCriteria: SuccessCriteria.Increase,
        experiments: [],
      };
    } else if (metric.measurementType) {
      return metric as AiGeneratedMetricDto;
    }

    console.error(`Unknown metric type: ${metric.type}`);
    console.error('metric', metric);
    return null;
  }

  /**
   * Append nanoid to ensure uniqueness. It's possible ChatGPT responds
   * with a key we already have. We also kebab-case it just in case ChatGPT
   * neglects our instructions.
   *
   * @param featureFlagName
   * @private
   */
  private static buildFlagKey(featureFlagName: string) {
    return `${featureFlagName.toLowerCase().replace(/\s/g, '-')}-${nanoid(6)}`;
  }

  private static scrubVariants(
    variants: Partial<AiGeneratedFlagVariantDto>[],
  ): AiGeneratedFlagVariantDto[] {
    if (!variants || !variants.length) {
      return DEFAULT_PREMAPPED_AI_VARIANTS;
    }

    const isBooleanVariant = typeof variants[0]?.value === 'boolean';

    /**
     * ChatGPT has responded with 3 variants for BOOL types (e.g. true, true, false),
     * with a rollout of 33%/33%/34% respectively. So just force boolean types
     * to only have 2 variants at 50%/%50.
     */
    if (isBooleanVariant) {
      const trueVariant =
        variants.find((v) => v.value === true) ?? DEFAULT_RAW_TRUE_VARIANT;
      const falseVariant =
        variants.find((v) => v.value === false) ?? DEFAULT_RAW_FALSE_VARIANT;

      return [trueVariant, falseVariant].map((v) =>
        AiExperimentDtoMapper.mapFlagVariant(v, 50),
      );
    }

    const distribution = distributeEqually(variants.length);

    return variants.map((variant: any, index: number) =>
      AiExperimentDtoMapper.mapFlagVariant(variant, distribution[index]),
    );
  }
}

/**
 * Given an integer n, distribute 100% equally among n values.
 * @param n
 */
export function distributeEqually(n: number) {
  const baseValue = Math.floor(100 / n);
  const remainder = 100 % n;

  const distribution = Array(n).fill(baseValue);

  for (let i = 0; i < remainder; i++) {
    distribution[i]++;
  }

  return distribution;
}

export function unKebabToSentence(kebab: string) {
  return kebab
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
